import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import PricingForm from '../components/PricingForm';
import PricingResults from '../components/PricingResults';
import ConsultationForm from '../components/ConsultationForm';
import ProposalViewer from '../components/ProposalViewer';
import '../styles/proposal-viewer.css';
import '../styles/pricing-calculator.css';
import '../styles/pricing-calculator-updated.css';
import '../styles/pdf-export.css';
import '../styles/chat-interface.css';
// No PDF library needed for text download
// No need for sanitizeInput since we removed the chat interface
import { processDocument } from '../utils/api-mock-updated';
// eslint-disable-next-line no-unused-vars
import { preprocessMarkdownForPdf, getFormattedDate, getCurrentYear } from '../utils/pdf-helpers';
import { generatePDF } from '../utils/pdf-generator';

// No need for API endpoint or mock flag as we're directly using processDocument

const PricingCalculator = ({ user, skipAuth = false }) => {
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [sessionId, setSessionId] = useState(null);
  const [proposalContent, setProposalContent] = useState(null);
  
  // State for form interface
  const [formResults, setFormResults] = useState(null);
  const [formData, setFormData] = useState(null);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showProposalViewer, setShowProposalViewer] = useState(false);

  // PDF generation is now handled directly by the generatePDF function
  // PDF generation is now handled by the simple-pdf.js implementation

  // Initialize session ID
  useEffect(() => {
    setSessionId(`session-${Date.now()}`);
  }, []);

  // Handle form submission
  const handleFormSubmit = (formData, costs) => {
    // Clear previous results first to ensure a clean state
    setFormResults(null);
    setProposalContent(null);
    setShowProposalViewer(false);
    
    // Set new form data and results
    setFormData(formData);
    setFormResults(costs);
  };

  // Handle consultation request
  const handleConsultationRequest = () => {
    setShowConsultationForm(true);
  };

  // Handle closing consultation form
  const handleCloseConsultation = () => {
    setShowConsultationForm(false);
  };

  // Handle generating proposal from form data
  const handleGenerateProposal = async () => {
    if (!formData || !formResults) return;
    
    // Clear any existing proposal content
    setProposalContent(null);
    setIsLoading(true);
    
    try {
      // Generate a new session ID to ensure a fresh context
      const proposalSessionId = `session-${Date.now()}`;
      
      // Use the API to generate a proposal based on form data
      const data = await processDocument({
        message: `Generate a proposal for a ${formData.projectType} project`,
        sessionId: proposalSessionId,
        context: {
          ...formData,
          complexity: formResults.complexity,
          useDeterministicPricing: true
        }
      });
      
      // Store proposal content
      if (data.proposal) {
        setProposalContent(data.proposal);
        setShowProposalViewer(true);
      }
    } catch (error) {
      console.error('Error generating proposal:', error);
      alert('Failed to generate proposal. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // No chat interface functions needed

  return (
    <Layout>
      <div className="pricing-calculator-page">
        <div className="hero-section">
          <h1>AI Project Pricing Calculator</h1>
          <p className="hero-subtitle">
            Get an instant estimate for your AI project. Provide details about your requirements for a more accurate quote.
          </p>
        </div>
        
        <div className="form-container">
          <PricingForm 
            onSubmit={handleFormSubmit} 
            onConsultationRequest={handleConsultationRequest}
            onProjectTypeChange={() => {
              setFormResults(null);
              setFormData(null);
            }}
          />
          
          {formResults && (
            <PricingResults 
              costs={formResults} 
              formData={formData} 
              onGenerateProposal={handleGenerateProposal}
              onConsultationRequest={handleConsultationRequest}
            />
          )}
        </div>
        
        {/* Consultation Form Modal */}
        {showConsultationForm && (
          <>
            <div className="modal-overlay" onClick={handleCloseConsultation}></div>
            <ConsultationForm 
              onClose={handleCloseConsultation} 
              projectType={formData?.projectType || null}
            />
          </>
        )}
        
        {/* Proposal Viewer */}
        {showProposalViewer && proposalContent && (
          <ProposalViewer 
            proposal={proposalContent}
            onClose={() => setShowProposalViewer(false)}
            onDownloadPdf={() => generatePDF(proposalContent)}
          />
        )}
      </div>
    </Layout>
  );
};

export default PricingCalculator;